import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, HeartPulse, ShoppingBag, Gamepad2, GraduationCap, Factory, ArrowUpRight } from 'lucide-react';

const industries = [
  { Icon: Landmark, name: 'BFSI', desc: 'PCI-DSS compliant hosting for banks, NBFCs & fintechs.', tag: 'Zero-downtime', color: 'from-cyan-500 to-blue-600' },
  { Icon: HeartPulse, name: 'Healthcare', desc: 'HIPAA-ready secure infrastructure for hospitals & HealthTech.', tag: 'HIPAA', color: 'from-rose-500 to-pink-600' },
  { Icon: ShoppingBag, name: 'E-commerce', desc: 'Auto-scaling for sale-day traffic spikes without breaking a sweat.', tag: 'Elastic', color: 'from-amber-500 to-orange-600' },
  { Icon: Gamepad2, name: 'Gaming', desc: 'Low-latency edge nodes and DDoS shield for online games.', tag: '<10ms', color: 'from-violet-500 to-fuchsia-600' },
  { Icon: GraduationCap, name: 'EdTech', desc: 'Reliable streaming and LMS hosting for millions of learners.', tag: 'High Concurrent', color: 'from-emerald-500 to-teal-600' },
  { Icon: Factory, name: 'Manufacturing', desc: 'IoT & Industry 4.0 workloads with on-prem hybrid cloud.', tag: 'Industry 4.0', color: 'from-slate-500 to-slate-700' }
];

const Industries = () => {
  return (
    <section className="section-padding section-mesh">
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-4"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Solutions By Industry</div>
          <h2 className="section-title">Built for every <span className="gradient-text">workload</span></h2>
          <p className="section-subtitle mx-auto mt-4">From regulated finance to real-time gaming — our infrastructure adapts to your industry’s toughest demands.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((ind, i) => (
            <motion.div
              key={ind.name}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ y: -8 }}
              className="group relative p-6 rounded-2xl glass hover:border-cyan-400/40 transition-all overflow-hidden"
            >
              <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full bg-gradient-to-br ${ind.color} opacity-20 group-hover:opacity-40 blur-2xl transition-opacity`} />
              <div className="relative">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${ind.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                  <ind.Icon className="text-white" size={24} />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-white">{ind.name}</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-400/30 uppercase tracking-wider">{ind.tag}</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{ind.desc}</p>
                <a href="/contact-us" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300 group-hover:gap-2 transition-all">
                  Explore solution <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;
